<template>
  <div 
    class="video-container" 
    ref="videoContainer" 
    tabindex="0"
    @focus="handleFocus"
    @blur="handleBlur"
    @click="handleVideoContainerClick"
  >
    <!-- ✅ CORRIGÉ : SimpleLoader seulement si on charge le poster -->
    <SimpleLoader v-if="posterSrc && !posterLoaded" />
    
    <!-- ✅ NOUVEAU : Image invisible pour détecter le chargement en amont -->
    <img 
      v-if="posterSrc"
      :src="posterSrc"
      style="display: none;"
      @load="handlePosterLoad"
      @error="handlePosterError"
      :key="posterSrc"
    />
    
    <!-- Un seul conteneur pour le poster (image ou fallback) -->
    <div
      v-if="!lecteurVideo"
      class="video-poster"
      :style="posterSrc && posterLoaded ? { backgroundImage: `url(${posterSrc})`, opacity: 1 } : { backgroundColor: 'rgba(0,0,0,0.7)' }"
      @click="togglePlayPause"
    >
      <div class="poster-play-icon">▶</div>
    </div>

    <!-- ✅ Message de chargement vidéo -->
    <SimpleLoader v-if="lecteurVideo && !videoLoaded" />

    <!-- ✅ Vidéo inchangée -->
    <video
      v-if="shouldLoadVideo"
      ref="videoPlayer"
      :src="videoSrc"
      class="video-player"
      :style="{ display: lecteurVideo ? 'block' : 'none' }"
      @loadeddata="handleVideoLoaded"
      @error="handleVideoError"
      @click="togglePlayPause"
      preload="metadata"
      muted
      playsinline
    ></video>
    
    <!-- ✅ Contrôles avec classe dynamique pour play/pause -->
    <div 
      v-if="lecteurVideo" 
      class="video-controls"
      :class="{ 'paused': isVideoPaused, 'playing': !isVideoPaused }"
      @click="togglePlayPause"
    >
      <!-- ✅ SUPPRIMÉ : Plus de texte émoji -->
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import SimpleLoader from '@/components/SimpleLoader.vue';

export default {
  name: 'LecteurVideo',
  components: {
    SimpleLoader
  },
  props: {
    videoSrc: String,
    resolution: String
  },
  data() {
    return {
      lecteurVideo: false,
      posterLoaded: false,
      videoLoaded: false,
      shouldLoadVideo: false,
      isVideoPaused: true,
      isVideoContainerFocused: false,
      isActiveVideo: false,
      pendingPlay: false
    };
  },
  computed: {
    ...mapState(['storeUrlArray', 'ficheOn']),

    posterSrc() { 
      if (!this.videoSrc) return null;
      
      const fileName = this.videoSrc.split('/').pop();
      const fileNameWithoutExt = fileName.replace('.mp4', '');
      const posterFileName = `${fileNameWithoutExt}-video.avif`;
      
      const resolution = this.resolution || 'desktop';
      const folder = resolution === 'mobile' ? '500px' : '1000px';
      
      return `diapo/${folder}/${posterFileName}`;
    }
  },
  methods: {
    handlePosterLoad() {
      console.log('image loader');
      console.log('✅ Image poster chargée avec succès');
      console.log('📁 Chemin:', this.posterSrc);
      
      this.posterLoaded = true;
      console.log('🖼️ posterLoaded mis à true');
      
      setTimeout(() => {
        this.shouldLoadVideo = true;
        console.log('🎥 Chargement vidéo déclenché');
      }, 200);
    },

    handlePosterError() {
      console.log('image loader');
      console.error('❌ Erreur de chargement du poster');
      console.error('📁 Chemin tentative:', this.posterSrc);
      
      this.posterLoaded = true;
      console.log('🖼️ posterLoaded mis à true (erreur)');
      
      this.shouldLoadVideo = true;
      console.log('🎥 Chargement vidéo forcé suite à erreur poster');
    },

    handleVideoLoaded() {
      this.videoLoaded = true;
      this.$emit('video-loaded', this.videoSrc);
      if (this.pendingPlay && this.$refs.videoPlayer) {
        this.$refs.videoPlayer.play().catch(() => {});
        this.isVideoPaused = false;
        this.pendingPlay = false;
      }
    },

    handleVideoError(event) {
      console.error('❌ Erreur de chargement de la vidéo:', event);
      this.videoLoaded = false;
    },

    togglePlayPause() {
      if (!this.lecteurVideo) {
        this.lecteurVideo = true;
        this.isVideoPaused = false;
        this.shouldLoadVideo = true; // ← Force l'affichage de la balise <video> immédiatement

        this.$nextTick(() => {
          // Tente de lancer la vidéo dès que possible
          if (this.$refs.videoPlayer) {
            this.$refs.videoPlayer.muted = true; // Nécessaire pour autoplay sur iOS
            this.$refs.videoPlayer.play().then(() => {
              this.isVideoPaused = false;
              this.$refs.videoPlayer.muted = false;
            }).catch(() => {
              this.pendingPlay = true;
            });
          } else {
            this.pendingPlay = true;
          }
        });
        return;
      }
      if (this.lecteurVideo && !this.videoLoaded) {
        this.pendingPlay = true;
        return;
      }
      if (this.lecteurVideo && this.$refs.videoPlayer) {
        this.$refs.videoPlayer.focus && this.$refs.videoPlayer.focus();
        if (this.$refs.videoPlayer.paused) {
          this.$refs.videoPlayer.play().then(() => {
            this.isVideoPaused = false;
            this.$refs.videoPlayer.muted = false;
          });
        } else {
          this.$refs.videoPlayer.pause();
          this.isVideoPaused = true;
        }
      }
    },

    // ✅ MODIFIÉ : Gestion des touches clavier avec vérification du statut actif
    handleKeydown(event) {
      const activeElement = document.activeElement;
      const isInFormElement = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.contentEditable === 'true' ||
        activeElement.isContentEditable
      );

      console.log('🎹 Touche pressée sur vidéo:', {
        key: event.key,
        code: event.code,
        isInFormElement,
        isActiveVideo: this.isActiveVideo,
        ficheOn: this.ficheOn,
        videoSrc: this.videoSrc
      });

      // ✅ NOUVEAU : Space fonctionne seulement si cette vidéo est active
      if (event.code === 'Space' && !isInFormElement && this.isActiveVideo && this.videoSrc) {
        event.preventDefault();
        event.stopPropagation();
        
        console.log('⌨️ Espace pressé - Control de la vidéo ACTIVE:', this.videoSrc);
        this.togglePlayPause();
        
        // Focus pour les interactions suivantes
        this.$refs.videoContainer.focus();
        return;
      }

      // ✅ Autres raccourcis nécessitent le focus
      if (!isInFormElement && this.isVideoContainerFocused) {
        switch (event.key) {
          case 'k':
          case 'K':
            event.preventDefault();
            this.togglePlayPause();
            console.log('⌨️ Touche K - Toggle play/pause');
            break;
          
          case 'Escape':
            event.preventDefault();
            if (this.lecteurVideo) {
              this.stopVideoPlayback();
              console.log('⌨️ Escape - Arrêt vidéo');
            }
            break;
        }
      }
    },

    // ✅ NOUVEAU : Méthode appelée par le parent pour définir le statut actif
    setActiveStatus(isActive) {
      this.isActiveVideo = isActive;
      if (isActive && !this.shouldLoadVideo) {
        this.shouldLoadVideo = true;
      }
      if (!isActive && this.lecteurVideo) {
        this.stopVideoPlayback();
      }
      console.log('setActiveStatus', this.videoSrc, isActive);
    },

    // ✅ NOUVEAU : Gestion du focus
    handleFocus() {
      this.isVideoContainerFocused = true;
      console.log('🎯 Container vidéo focusé');
    },

    handleBlur() {
      this.isVideoContainerFocused = false;
      console.log('🎯 Container vidéo défocusé');
    },

    // ✅ NOUVEAU : Focus automatique lors du clic
    handleVideoContainerClick() {
      this.$refs.videoContainer.focus();
      console.log('🎯 Focus automatique sur le container');
    },

    startVideoPlayback() {
      console.log('🎬 startVideoPlayback appelé');
      if (this.$refs.videoPlayer && this.videoLoaded) {
        this.$refs.videoPlayer.currentTime = 0;
        this.$refs.videoPlayer.play();
        this.lecteurVideo = true;
        this.isVideoPaused = false;
      }
    },

    stopVideoPlayback() {
      console.log('⏹️ stopVideoPlayback appelé');
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
        this.$refs.videoPlayer.currentTime = 0;
      }
      this.lecteurVideo = false;
      this.isVideoPaused = true;
    },

    resetLoadingStates() {
      console.log('🔄 Réinitialisation des états de chargement');
      this.posterLoaded = false;
      this.videoLoaded = false;
      this.shouldLoadVideo = false;
      this.lecteurVideo = false;
      this.isVideoPaused = true;
      this.isActiveVideo = false; // ✅ NOUVEAU : Reset du statut actif
    },

    // ✅ NOUVEAU : Force l'activation de la première vidéo
    forceActivateFirstVideo() {
      console.log('🔧 Force activation première vidéo');
      
      if (this.currentIndex === 0 && this.isCurrentSlideVideo) {
        const firstVideoRef = this.$refs['video-0'];
        
        if (firstVideoRef) {
          const component = Array.isArray(firstVideoRef) ? firstVideoRef[0] : firstVideoRef;
          if (component && typeof component.setActiveStatus === 'function') {
            component.setActiveStatus(true);
            console.log('✅ Première vidéo forcée comme active');
          }
        }
      }
    },
  },
  
  watch: {
    posterSrc(newValue, oldValue) {
      console.log('🔄 posterSrc changé:', { old: oldValue, new: newValue });
      if (newValue !== oldValue) {
        this.posterLoaded = false;
        console.log('🖼️ posterLoaded remis à false');
      }
    },

    posterLoaded(newValue) {
      console.log('🖼️ posterLoaded changé:', newValue);
      if (newValue) {
        console.log('✅ Le poster devrait maintenant être visible');
      }
    },

    // ✅ NOUVEAU : Watch pour surveiller isVideoPaused
    isVideoPaused(newValue) {
      console.log('🎬 isVideoPaused changé:', newValue);
      console.log('🎨 Classes CSS actives:', {
        paused: newValue,
        playing: !newValue
      });
    },

    lecteurVideo(newValue) {
      console.log('👁️ lecteurVideo watch:', newValue);
      
      if (newValue && this.$refs.videoPlayer) {
        if (this.videoLoaded) {
          // ✅ Ajout : forcer la lecture même si pendingPlay
          if (this.$refs.videoPlayer.paused) {
            this.$refs.videoPlayer.play().catch(() => {});
          }
          this.isVideoPaused = false;
        } else {
          const checkVideoLoaded = () => {
            if (this.videoLoaded && this.$refs.videoPlayer) {
              if (this.$refs.videoPlayer.paused) {
                this.$refs.videoPlayer.play().catch(() => {});
              }
              this.isVideoPaused = false;
            } else if (this.lecteurVideo) {
              setTimeout(checkVideoLoaded, 100);
            }
          };
          setTimeout(checkVideoLoaded, 100);
        }
      } else if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
        this.isVideoPaused = true;
      }
    },
    
    'ficheOn'(newValue) {
      if (newValue === false) {
        this.stopVideoPlayback();
        console.log('⏹️ Lecture vidéo arrêtée : mode différent de modefiche');
      }
    },

    videoSrc(newValue, oldValue) {
      console.log('🔄 videoSrc changé:', { old: oldValue, new: newValue });
      this.resetLoadingStates();
    }
  },

  mounted() {
    console.log('🚀 LecteurVideo monté avec videoSrc:', this.videoSrc);
    console.log('🖼️ posterSrc calculé:', this.posterSrc);
    this.resetLoadingStates();

    // ✅ NOUVEAU : Ajouter les listeners de clavier
    document.addEventListener('keydown', this.handleKeydown);
    console.log('⌨️ Listeners clavier ajoutés pour:', this.videoSrc);
  },

  beforeUnmount() {
    this.stopVideoPlayback();
    
    // ✅ NOUVEAU : Nettoyer les listeners
    document.removeEventListener('keydown', this.handleKeydown);
    console.log('⌨️ Listeners clavier supprimés pour:', this.videoSrc);
  }
};
</script>

<style scoped>
.video-container {
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: black;
  outline: none;
  color: white;
  width: calc(100% - 10px);
  height: calc(100% - 10px);
  cursor: pointer;
  border: 2px solid white;
}

.video-container:focus {
  outline: 1px solid rgba(255, 255, 255, 0.3);
  outline-offset: -1px;
}

.video-player {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
  z-index: 2;
}

.video-poster,
.video-poster-fallback {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
}

.video-poster {
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
}

.video-poster-fallback {
  background-color: rgba(0, 0, 0, 0.7);
  flex-direction: column;
}

.poster-play-icon {
  font-size: 48px;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.fallback-text {
  color: white;
  font-size: 18px;
  margin-top: 8px;
}

.video-controls {
  position: absolute;
  width: 8%;
  height: 8%;
  min-width: 40px;
  min-height: 40px;
  max-width: 80px;
  max-height: 80px;
  left: 50%;
  transform: translateX(-50%);
  bottom: 60px;
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  opacity: 0.7;
  z-index: 3;
  transition: all 0.3s ease;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
}

.video-controls.paused {
  background-image: url('/public/interface/play.svg');
}

.video-controls.playing {
  background-image: url('/public/interface/pause.svg');
}

.video-controls:hover,
.video-controls.active {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.8);
}
</style>